import React, { useCallback, useContext } from "react";

import { faPause, faPlay, faStop } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";

import { confirm } from "~/lib/prompts";
import OnAirIcon from "~/meetingRecorder/OnAirIcon";
import MeetingPhaseContext, { type CopilotStatus } from "~/stream/MeetingPhaseContext";
import { Tooltip } from "~/ui";
import Menu from "~/ui/menu";

import buttonStyles from "~/ui/button/Button.module.css";

import noteHeaderStyles from "../NoteHeader.module.css";
import styles from "./InMeetingControls.module.css";
import sharedStyles from "./RecordingControls.module.css";

export default function InMeetingControls({
	copilotStatus,
}: {
	copilotStatus: Extract<
		CopilotStatus,
		{ type: "recording"; recordingStartedAt: Date } | { type: "paused"; recordingStartedAt: Date }
	>;
}) {
	const { pauseRecording, resumeRecording, stopRecording } = useContext(MeetingPhaseContext);

	const onStopRecordingClick = useCallback(async () => {
		const confirmText = <p>The meeting recording and transcript up until this point will be saved.</p>;
		if (
			!(await confirm(confirmText, {
				yesText: "Stop",
				yesProps: { variant: "primary" },
				title: "Stop recording?",
			}))
		) {
			return;
		}
		stopRecording();
	}, [stopRecording]);

	const onPauseRecordingClick = useCallback(() => {
		pauseRecording();
	}, [pauseRecording]);

	const onResumeRecordingClick = useCallback(() => {
		resumeRecording();
	}, [resumeRecording]);

	const tooltipContents: { [key in typeof copilotStatus.type]?: React.ReactNode } = {
		recording: "Recording. Click to pause.",
		paused: "Recording is paused",
	};

	const iconContents: { [key in typeof copilotStatus.type]: React.ReactNode } = {
		recording: <OnAirIcon />,
		paused: <FontAwesomeIcon icon={faPause} fixedWidth className={cx(styles.icon, styles.pausedIcon)} />,
	};

	const textContents: { [key in typeof copilotStatus.type]: React.ReactNode } = {
		recording: "REC",
		paused: "Paused",
	};

	const backgroundColors: { [key in typeof copilotStatus.type]?: string } = {
		recording: "transparent",
	};

	const menuContent: { [key in typeof copilotStatus.type]?: React.ReactNode } = {
		paused: (
			<Menu.Item onSelect={onResumeRecordingClick}>
				<span className={styles.recordingControlMenuItem}>
					<FontAwesomeIcon icon={faPlay} className={styles.recordingControlButtonIcon} fixedWidth />
					Resume
				</span>
			</Menu.Item>
		),
		recording: (
			<Menu.Item onSelect={onPauseRecordingClick}>
				<span className={styles.recordingControlMenuItem}>
					<FontAwesomeIcon icon={faPause} className={styles.recordingControlButtonIcon} fixedWidth />
					Pause
				</span>
			</Menu.Item>
		),
	};

	return (
		<Menu>
			<AnimatePresence>
				<Tooltip
					placement="top"
					maxWidth={250}
					className={sharedStyles.smallTooltip}
					content={tooltipContents[copilotStatus.type]}
				>
					<Menu.Trigger asChild>
						<motion.button
							className={cx(
								noteHeaderStyles.ultraSlimButton,
								buttonStyles.button,
								styles.button,
								styles[copilotStatus.type],
							)}
							layout
							style={{ borderRadius: 6 }} // Prevents the border radius from being distorted during the animation
							initial={{ opacity: 0 }}
							animate={{
								backgroundColor: backgroundColors[copilotStatus.type] || "var(--secondaryButtonColor)",
								opacity: 1,
							}}
							transition={{
								layout: {
									ease: "easeOut",
									duration: 0.3,
								},
							}}
							exit={{ opacity: 0 }}
						>
							<motion.span layout="position" style={{ display: "flex" }}>
								{iconContents[copilotStatus.type]}
							</motion.span>
							<motion.span
								className={cx(sharedStyles.buttonContent, {
									[sharedStyles.hideIfNoSpace]: copilotStatus.type !== "recording",
								})}
								layout="position"
							>
								{textContents[copilotStatus.type]}
							</motion.span>
						</motion.button>
					</Menu.Trigger>
				</Tooltip>
			</AnimatePresence>
			<Menu.Content className={styles.recordingControlMenu} align="center">
				{menuContent[copilotStatus.type]}
				<hr className={styles.recordingControlMenuSeparator} />
				<Menu.Item onSelect={onStopRecordingClick} className={styles.stopControlButton}>
					<span className={styles.recordingControlMenuItem}>
						<FontAwesomeIcon icon={faStop} className={styles.recordingControlButtonIcon} fixedWidth />
						Stop
					</span>
				</Menu.Item>
			</Menu.Content>
		</Menu>
	);
}
